'use client';
import * as React from 'react';
import type { RefObject } from '@mui/x-internals/types';
import { throttle } from '@mui/x-internals/throttle';
import { isDeepEqual } from '@mui/x-internals/isDeepEqual';
import useEventCallback from '@mui/utils/useEventCallback';
import debounce from '@mui/utils/debounce';
import {
  useGridEvent,
  gridSortModelSelector,
  gridFilterModelSelector,
  gridRowNodeSelector,
  GRID_ROOT_GROUP_ID,
  gridPaginationModelSelector,
  gridRowIdSelector,
  useGridSelector,
  gridFilteredSortedRowIdsSelector,
  gridExpandedSortedRowIdsSelector,
  gridRowSelector,
} from '@mui/x-data-grid';
import type {
  GridGroupNode,
  GridSkeletonRowNode,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridLeafNode,
  GridGetRowsResponse,
  GridDataSourceGroupNode,
  GridRowTreeConfig,
} from '@mui/x-data-grid';
import {
  buildRootGroup,
  getVisibleRows,
  gridRenderContextSelector,
  GridStrategyGroup,
  useGridRegisterStrategyProcessor,
  runIf,
  DataSourceRowsUpdateStrategy,
  useGridDataSourceFilterModelChange,
} from '@mui/x-data-grid/internals';
import type { GridStrategyProcessor, GridTreeDepths } from '@mui/x-data-grid/internals';
import type { GridGetRowsParamsPro as GridGetRowsParams } from '../dataSource/models';
import type { GridPrivateApiPro } from '../../../models/gridApiPro';
import type { DataGridProProcessedProps } from '../../../models/dataGridProProps';
import { findSkeletonRowsSection } from '../lazyLoader/utils';
import { GRID_SKELETON_ROW_ROOT_ID } from '../lazyLoader/useGridLazyLoaderPreProcessors';
import { checkGroupChildrenExpansion } from '../../../utils/tree/utils';

type AdjustRowParams = Partial<Omit<GridGetRowsParams, 'start' | 'end'>> & {
  start: GridGetRowsParams['start'];
  end: GridGetRowsParams['end'];
};

type GridGetRowsParamsWithGrouping = GridGetRowsParams & {
  groupFields?: string[];
  pivotModel?: unknown;
};

interface FetchSkeletonRowsOptions {
  /**
   * When `true`, the scan only fetches visible skeleton rows and skips the fallback
   * revalidation path that normally runs when the viewport is already fully loaded.
   * This is used after auto-expanding freshly inserted groups: if the expansion did
   * not expose visible skeleton rows, there is no extra lazy-loading work to do.
   */
  skipFallbackRevalidation?: boolean;
}

const INTERVAL_CACHE_INITIAL_STATE = {
  firstRowToRender: 0,
  lastRowToRender: 0,
};

const GRID_SKELETON_ROW_NESTED_ID = 'auto-generated-skeleton-row-nested';

const getSkeletonRowId = (index: number) => `${GRID_SKELETON_ROW_ROOT_ID}-${index}`;
const getSkeletonNestedRowId = (index: number, parentId: GridRowId) =>
  `${GRID_SKELETON_ROW_NESTED_ID}-${parentId}-${index}`;

/**
 * Removes a row and its entire subtree from the working `tree`/`dataRowIdToModelLookup`
 * copies. Ids in `skip` are left untouched, so a row that moved within the replaced range
 * (and was re-added this pass) is kept.
 */
const deleteRowAndDescendants = (
  tree: GridRowTreeConfig,
  dataRowIdToModelLookup: Record<GridRowId, GridRowModel>,
  rowId: GridRowId,
  skip?: Set<GridRowId>,
) => {
  if (skip?.has(rowId)) {
    return;
  }
  const node = tree[rowId];
  if (node?.type === 'group') {
    node.children.forEach((childId) =>
      deleteRowAndDescendants(tree, dataRowIdToModelLookup, childId, skip),
    );
  }
  delete tree[rowId];
  delete dataRowIdToModelLookup[rowId];
};

/**
 * Counts the nodes of the tree per depth. The loader builds the tree by hand, so it must also
 * keep `treeDepths` in sync. Features like row selection propagation read the maximum tree
 * depth from it.
 */
const computeTreeDepths = (tree: GridRowTreeConfig) => {
  const treeDepths: GridTreeDepths = {};
  Object.values(tree).forEach((node) => {
    if (node.id === GRID_ROOT_GROUP_ID) {
      return;
    }
    treeDepths[node.depth] = (treeDepths[node.depth] ?? 0) + 1;
  });
  return treeDepths;
};

/**
 * Drops the children past `rowCount` and their subtrees. Mutates `children` and
 * `childrenFromPath`. Ids in `skip` were re-added by the current pass, so they only lose their
 * stale trailing position.
 */
const trimChildrenToRowCount = (
  tree: GridRowTreeConfig,
  dataRowIdToModelLookup: Record<GridRowId, GridRowModel>,
  children: GridRowId[],
  childrenFromPath: Record<string, Record<string, GridRowId>> | undefined,
  rowCount: number | undefined,
  skip?: Set<GridRowId>,
) => {
  if (rowCount === undefined || rowCount < 0 || children.length <= rowCount) {
    return;
  }

  const removedIds = children.splice(rowCount);
  removedIds.forEach((rowId) => deleteRowAndDescendants(tree, dataRowIdToModelLookup, rowId, skip));

  if (childrenFromPath) {
    const removedIdSet = new Set(removedIds.filter((rowId) => !skip?.has(rowId)));
    Object.keys(childrenFromPath).forEach((groupingField) => {
      const rowIdByGroupingKey = childrenFromPath[groupingField];
      const staleKeys = Object.keys(rowIdByGroupingKey).filter((groupingKey) =>
        removedIdSet.has(rowIdByGroupingKey[groupingKey]),
      );
      if (staleKeys.length === 0) {
        return;
      }
      // The per-field records are shared with the previous tree, so replace instead of mutating.
      const nextRowIdByGroupingKey = { ...rowIdByGroupingKey };
      staleKeys.forEach((groupingKey) => delete nextRowIdByGroupingKey[groupingKey]);
      childrenFromPath[groupingField] = nextRowIdByGroupingKey;
    });
  }
};

/**
 * @requires useGridRows (state)
 * @requires useGridPagination (state)
 * @requires useGridScroll (method
 */
export const useGridDataSourceNestedLazyLoader = (
  privateApiRef: RefObject<GridPrivateApiPro>,
  props: Pick<
    DataGridProProcessedProps,
    | 'dataSource'
    | 'lazyLoading'
    | 'lazyLoadingRequestThrottleMs'
    | 'treeData'
    | 'dataSourceRevalidateMs'
    | 'defaultGroupingExpansionDepth'
    | 'isGroupExpandedByDefault'
  >,
): void => {
  const isDataNested = useGridSelector(privateApiRef, () =>
    props.treeData
      ? true
      : ((
          privateApiRef.current.unstable_applyPipeProcessors(
            'getRowsParams',
            {},
          ) as Partial<GridGetRowsParams> & { groupFields: string[] }
        )?.groupFields?.length ?? 0) > 0,
  );
  const setStrategyAvailability = React.useCallback(() => {
    privateApiRef.current.setStrategyAvailability(
      GridStrategyGroup.DataSource,
      DataSourceRowsUpdateStrategy.LazyLoadedGroupedData,
      props.dataSource && props.lazyLoading && isDataNested ? () => true : () => false,
    );
  }, [privateApiRef, props.lazyLoading, props.dataSource, isDataNested]);

  const [isStrategyActive, setIsStrategyActive] = React.useState(false);
  const renderedRowsIntervalCache = React.useRef(INTERVAL_CACHE_INITIAL_STATE);
  const rowsStale = React.useRef<boolean>(false);
  const draggedRowId = React.useRef<GridRowId | null>(null);
  const pollingIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  // Snapshot of the row tree taken right before a sort/filter triggered reset.
  // Used by nested data updates that fire later in the auto-expansion chain so
  // they can preserve expansion state below the first level.
  const previousTreeRef = React.useRef<GridRowTreeConfig | null>(null);

  const fetchRows = React.useCallback(
    (params: Partial<GridGetRowsParams>) => {
      privateApiRef.current.dataSource.fetchRows(GRID_ROOT_GROUP_ID, params);
    },
    [privateApiRef],
  );

  const debouncedFetchRows = React.useMemo(() => debounce(fetchRows, 0), [fetchRows]);

  const hasFilterModelChanged = useGridDataSourceFilterModelChange(privateApiRef);

  // Adjust the render context range to fit the pagination model's page size
  // First row index should be decreased to the start of the page, end row index should be increased to the end of the page
  const adjustRowParams = React.useCallback(
    (params: AdjustRowParams) => {
      if (typeof params.start !== 'number') {
        return params;
      }

      const paginationModel = gridPaginationModelSelector(privateApiRef);

      return {
        ...params,
        start: params.start - (params.start % paginationModel.pageSize),
        end: params.end + paginationModel.pageSize - (params.end % paginationModel.pageSize) - 1,
      };
    },
    [privateApiRef],
  );

  const getChildrenCount = props.dataSource?.getChildrenCount;
  const getGroupKey = props.dataSource?.getGroupKey;

  const resetRowTree = React.useCallback(() => {
    const previousTree = privateApiRef.current.state.rows.tree;

    privateApiRef.current.caches.rows.dataRowIdToModelLookup = {};
    privateApiRef.current.setState((state) => ({
      ...state,
      rows: {
        ...state.rows,
        tree: { [GRID_ROOT_GROUP_ID]: buildRootGroup() },
        treeDepths: {},
        dataRowIds: [],
        dataRowIdToModelLookup: {},
        groupsToFetch: [],
      },
    }));
    privateApiRef.current.publishEvent('rowsSet');

    return previousTree;
  }, [privateApiRef]);

  const revalidateRows = useEventCallback((firstRowIndex: number, lastRowIndex: number) => {
    if (rowsStale.current || lastRowIndex <= firstRowIndex) {
      return;
    }

    const expandedSortedRowIds = gridExpandedSortedRowIdsSelector(privateApiRef);
    const sortModel = gridSortModelSelector(privateApiRef);
    const filterModel = gridFilterModelSelector(privateApiRef);
    const rowRangesByParent = new Map<GridRowId, { start: number; end: number }>();

    for (let i = firstRowIndex; i < lastRowIndex && i < expandedSortedRowIds.length; i += 1) {
      const rowId = expandedSortedRowIds[i];
      const rowNode = gridRowNodeSelector(privateApiRef, rowId);
      if (!rowNode || rowNode.type === 'skeletonRow' || rowNode.parent == null) {
        continue;
      }

      const parentNode = gridRowNodeSelector(privateApiRef, rowNode.parent) as GridGroupNode | null;
      if (!parentNode || parentNode.type !== 'group') {
        continue;
      }

      const childIndex = parentNode.children.indexOf(rowId);
      if (childIndex === -1) {
        continue;
      }

      const existingRange = rowRangesByParent.get(rowNode.parent);
      if (existingRange) {
        existingRange.start = Math.min(existingRange.start, childIndex);
        existingRange.end = Math.max(existingRange.end, childIndex);
      } else {
        rowRangesByParent.set(rowNode.parent, { start: childIndex, end: childIndex });
      }
    }

    rowRangesByParent.forEach((range, parentId) => {
      if (parentId === GRID_ROOT_GROUP_ID) {
        debouncedFetchRows(
          adjustRowParams({
            start: range.start,
            end: range.end,
            sortModel,
            filterModel,
            groupKeys: [],
          }),
        );
        return;
      }

      privateApiRef.current.dataSource.fetchRows(parentId, {
        ...adjustRowParams({
          start: range.start,
          end: range.end,
          sortModel,
          filterModel,
        }),
        showChildrenLoading: false,
      });
    });
  });

  const stopPolling = React.useCallback(() => {
    if (pollingIntervalRef.current !== null) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const startPolling = useEventCallback(() => {
    stopPolling();

    if (props.dataSourceRevalidateMs <= 0) {
      return;
    }

    pollingIntervalRef.current = setInterval(() => {
      const { firstRowToRender, lastRowToRender } = renderedRowsIntervalCache.current;
      revalidateRows(firstRowToRender, lastRowToRender);
    }, props.dataSourceRevalidateMs);
  });

  /**
   * Drops a parent's children past `rowCount`, for the paths where `replaceNestedRows` has no
   * row to replace with: an in-place update, or a response with no row at all.
   */
  const pruneRowsToRowCount = React.useCallback(
    (parentId: GridRowId, rowCount: number | undefined) => {
      if (rowCount === undefined || rowCount < 0) {
        return;
      }

      // Guard before cloning: this runs on every poll response, and most of them prune nothing.
      const currentTree = privateApiRef.current.state.rows.tree;
      const parentNode = currentTree[parentId] as GridGroupNode | undefined;
      if (parentNode?.type !== 'group' || parentNode.children.length <= rowCount) {
        return;
      }

      const tree = { ...currentTree };
      const dataRowIdToModelLookup = { ...privateApiRef.current.state.rows.dataRowIdToModelLookup };
      const children = [...parentNode.children];
      const childrenFromPath = Object.assign(
        Object.create(null),
        (parentNode as GridDataSourceGroupNode).childrenFromPath,
      );
      trimChildrenToRowCount(tree, dataRowIdToModelLookup, children, childrenFromPath, rowCount);

      const prunedNode = { ...parentNode, children, childrenFromPath } as GridDataSourceGroupNode;
      if (parentId !== GRID_ROOT_GROUP_ID) {
        prunedNode.serverChildrenCount = rowCount;
      }
      tree[parentId] = prunedNode;

      privateApiRef.current.caches.rows.dataRowIdToModelLookup = dataRowIdToModelLookup;

      privateApiRef.current.setState((state) => ({
        ...state,
        rows: {
          ...state.rows,
          dataRowIdToModelLookup,
          dataRowIds: state.rows.dataRowIds.filter((id) => tree[id] !== undefined),
          tree,
          totalRowCount: parentId === GRID_ROOT_GROUP_ID ? rowCount : state.rows.totalRowCount,
        },
      }));
      privateApiRef.current.publishEvent('rowsSet');
    },
    [privateApiRef],
  );

  const addRootSkeletonRows = React.useCallback(() => {
    const pageRowCount = privateApiRef.current.state.pagination.rowCount;

    // A shrinking row count leaves rows and skeletons past the end. `> 0` guards against an
    // unknown count wiping the loaded rows; a real drop to 0 comes from the response instead.
    if (pageRowCount > 0) {
      pruneRowsToRowCount(GRID_ROOT_GROUP_ID, pageRowCount);
    }

    const tree = { ...privateApiRef.current.state.rows.tree };
    const rootGroup = tree[GRID_ROOT_GROUP_ID] as GridGroupNode;
    const rootGroupChildren = [...rootGroup.children];

    const rootChildrenCount = rootGroupChildren.length;

    if (rootChildrenCount === 0) {
      return;
    }

    let hasChanged = false;

    // Nested Lazy loading only support VIEWPORT loading trigger, no need for a specific check
    for (let i = 0; i < pageRowCount - rootChildrenCount; i += 1) {
      const skeletonId = getSkeletonRowId(i + rootChildrenCount);
      rootGroupChildren.push(skeletonId);

      const skeletonRowNode: GridSkeletonRowNode = {
        type: 'skeletonRow',
        id: skeletonId,
        parent: GRID_ROOT_GROUP_ID,
        depth: 0,
      };

      tree[skeletonId] = skeletonRowNode;
      hasChanged = true;
    }

    if (!hasChanged) {
      return;
    }

    tree[GRID_ROOT_GROUP_ID] = { ...rootGroup, children: rootGroupChildren };

    privateApiRef.current.setState(
      (state) => ({
        ...state,
        rows: {
          ...state.rows,
          tree,
          treeDepths: computeTreeDepths(tree),
        },
      }),
      'addSkeletonRows',
    );
    privateApiRef.current.publishEvent('rowsSet');
  }, [privateApiRef, pruneRowsToRowCount]);

  const findSkeletonSectionAndFetchRows = React.useCallback(
    (firstRowIndex: number, lastRowIndex: number, options: FetchSkeletonRowsOptions = {}) => {
      const sortModel = gridSortModelSelector(privateApiRef);
      const filterModel = gridFilterModelSelector(privateApiRef);
      const currentVisibleRows = getVisibleRows(privateApiRef);

      const skeletonRowsSection = findSkeletonRowsSection({
        apiRef: privateApiRef,
        visibleRows: currentVisibleRows.rows,
        range: {
          firstRowIndex,
          lastRowIndex,
        },
      });

      if (!skeletonRowsSection) {
        if (!options.skipFallbackRevalidation) {
          revalidateRows(firstRowIndex, lastRowIndex);
          startPolling();
        }
        return false;
      }

      const expandedSortedRowIds = gridExpandedSortedRowIdsSelector(privateApiRef);
      const skeletonNodesGroupedByParents = new Map<GridRowId, GridRowId[]>();

      // Group skeleton rows by their parent
      for (
        let i = skeletonRowsSection.firstRowIndex;
        i <= skeletonRowsSection.lastRowIndex;
        i += 1
      ) {
        const rowId = expandedSortedRowIds[i];
        const rowNode = gridRowNodeSelector(privateApiRef, rowId);
        if (rowNode?.type === 'skeletonRow' && rowNode.parent != null) {
          if (!skeletonNodesGroupedByParents.has(rowNode.parent)) {
            skeletonNodesGroupedByParents.set(rowNode.parent, []);
          }
          skeletonNodesGroupedByParents.get(rowNode.parent)!.push(rowId);
        }
      }

      // Process each parent group separately
      skeletonNodesGroupedByParents.forEach((skeletonIds, parentId) => {
        const parentNode = gridRowNodeSelector(privateApiRef, parentId) as GridGroupNode;
        if (!parentNode) {
          return;
        }
        const parentChildren = parentNode.children;

        // Find the first and last skeleton row indexes relative to parent
        const firstSkeletonIdx = parentChildren.indexOf(skeletonIds[0]);
        const lastSkeletonIdx = parentChildren.indexOf(skeletonIds[skeletonIds.length - 1]);

        if (firstSkeletonIdx === -1 || lastSkeletonIdx === -1) {
          return;
        }

        if (parentId === GRID_ROOT_GROUP_ID) {
          debouncedFetchRows(
            adjustRowParams({
              start: firstSkeletonIdx,
              end: lastSkeletonIdx,
              sortModel,
              filterModel,
              groupKeys: [],
            }),
          );
        } else {
          privateApiRef.current.dataSource.fetchRows(
            parentId,
            adjustRowParams({
              start: firstSkeletonIdx,
              end: lastSkeletonIdx,
            }),
          );
        }
      });

      return true;
    },
    [privateApiRef, debouncedFetchRows, adjustRowParams, revalidateRows, startPolling],
  );

  const fetchVisibleSkeletonRows = React.useCallback(
    (options: FetchSkeletonRowsOptions = {}) => {
      const cachedInterval = renderedRowsIntervalCache.current;
      let firstRowIndex = cachedInterval.firstRowToRender;
      let lastRowIndex = cachedInterval.lastRowToRender;

      if (lastRowIndex <= firstRowIndex) {
        const renderContext = gridRenderContextSelector(privateApiRef);
        firstRowIndex = renderContext.firstRowIndex;
        lastRowIndex = renderContext.lastRowIndex;
      }

      if (lastRowIndex <= firstRowIndex) {
        return false;
      }

      return findSkeletonSectionAndFetchRows(firstRowIndex, lastRowIndex, options);
    },
    [privateApiRef, findSkeletonSectionAndFetchRows],
  );

  const cleanUpParentNodeAndGenerateSkeletonRows = React.useCallback(
    (parentId: GridRowId) => {
      const dataRowIdToModelLookup = { ...privateApiRef.current.state.rows.dataRowIdToModelLookup };
      const tree = { ...privateApiRef.current.state.rows.tree };

      const deletedIds = new Set<GridRowId>();

      const rowNode = gridRowNodeSelector(privateApiRef, parentId);
      if (!rowNode || rowNode.type !== 'group' || rowNode.children.length === 0) {
        return;
      }

      // Remove current descendants from the tree
      const traverse = (nodeId: GridRowId) => {
        const node = gridRowNodeSelector(privateApiRef, nodeId);
        if (!node) {
          return;
        }

        // Recursively traverse children first (depth-first)
        if (node.type === 'group' && node.children.length > 0) {
          node.children.forEach(traverse);
        }
        if (deletedIds.has(nodeId)) {
          return;
        }
        deletedIds.add(nodeId);
        delete dataRowIdToModelLookup[nodeId];
        delete tree[nodeId];
      };

      rowNode.children.forEach(traverse);

      // Add skeleton rows for the children
      const rowModel = gridRowSelector(privateApiRef, parentId);
      if (!rowModel) {
        return;
      }
      const childrenCount = getChildrenCount?.(rowModel) ?? 0;
      if (childrenCount <= 0) {
        return;
      }

      const newChildren: GridRowId[] = [];

      for (let i = 0; i < childrenCount; i += 1) {
        const skeletonId = getSkeletonNestedRowId(i, parentId);
        if (tree[skeletonId]) {
          newChildren.push(skeletonId);
          continue;
        }
        tree[skeletonId] = {
          type: 'skeletonRow',
          id: skeletonId,
          parent: parentId,
          depth: rowNode.depth + 1,
        };
        newChildren.push(skeletonId);
      }

      tree[parentId] = {
        ...rowNode,
        children: newChildren,
      } as GridGroupNode;

      privateApiRef.current.caches.rows.dataRowIdToModelLookup = dataRowIdToModelLookup;

      privateApiRef.current.setState((state) => ({
        ...state,
        rows: {
          ...state.rows,
          tree,
          treeDepths: computeTreeDepths(tree),
          dataRowIdToModelLookup,
          dataRowIds: state.rows.dataRowIds.filter((id) => !deletedIds.has(id)),
        },
      }));
      privateApiRef.current.publishEvent('rowsSet');
    },
    [privateApiRef, getChildrenCount],
  );

  const replaceNestedRows = React.useCallback(
    (
      startIndex: number,
      response: GridGetRowsResponse,
      fetchParams: GridGetRowsParams,
      parentId: GridRowId = GRID_ROOT_GROUP_ID,
      previousTree?: GridRowTreeConfig,
    ) => {
      if (response.rows.length === 0) {
        return false;
      }

      const currentTree = privateApiRef.current.state.rows.tree;
      const tree = { ...currentTree };
      const treeToReadPreviousExpansionState = previousTree ?? currentTree;
      const dataRowIdToModelLookup = { ...privateApiRef.current.state.rows.dataRowIdToModelLookup };
      const targetGroup = tree[parentId] as GridGroupNode;
      const targetGroupChildren = [...targetGroup.children];
      const targetGroupChildrenFromPath = Object.assign(
        Object.create(null),
        targetGroup.childrenFromPath,
      );
      const seenIds = new Set<GridRowId>();

      if (!getGroupKey) {
        throw new Error('MUI X: No `getGroupKey` method provided with the dataSource.');
      }

      if (!getChildrenCount) {
        throw new Error('MUI X: No `getChildrenCount` method provided with the dataSource.');
      }

      // Calculate parent depth (-1 for root, so children at root level have depth 0)
      const parentDepth = parentId === GRID_ROOT_GROUP_ID ? -1 : targetGroup.depth;
      const parentPath: string[] =
        parentId === GRID_ROOT_GROUP_ID
          ? []
          : ((tree[parentId] as GridDataSourceGroupNode).path ?? []);
      const groupFields =
        props.treeData || !('groupFields' in fetchParams)
          ? []
          : ((fetchParams as GridGetRowsParamsWithGrouping).groupFields ?? []);
      // The Premium pivoting pipe processor adds `pivotModel` only while pivoting is active.
      // Match the non-lazy row grouping path by capping default expansion at the last
      // row-grouping level when pivoting is active.
      const isPivotingActive =
        !props.treeData && (fetchParams as GridGetRowsParamsWithGrouping).pivotModel != null;
      const maxDepth = isPivotingActive ? groupFields.length - 1 : undefined;
      let hasExpandedGroupToFetch = false;

      for (let i = 0; i < response.rows.length; i += 1) {
        const rowModel = response.rows[i];
        const childrenCount = getChildrenCount(rowModel);
        if (childrenCount === -1) {
          throw new Error(
            'MUI X Data Grid: Nested lazy loading does not support unknown children count for now.\nIf this is a use-case that you are interested in, please open an issue: https://github.com/mui/mui-x/issues/new?template=2.feature.yml',
          );
        }
        const rowId = gridRowIdSelector(privateApiRef, rowModel);
        const [removedRowId] = targetGroupChildren.splice(startIndex + i, 1, rowId);

        // Drop the replaced row and any subtree it owned (skipping rows re-added this
        // pass) so a changed id swapping out an expanded group leaves no orphans behind.
        deleteRowAndDescendants(tree, dataRowIdToModelLookup, removedRowId, seenIds);

        let rowTreeNodeConfig: GridLeafNode | GridDataSourceGroupNode;

        const groupingKey = getGroupKey(rowModel);
        const previousNode = treeToReadPreviousExpansionState[rowId];
        const previousChildrenExpanded =
          previousNode?.type === 'group' ? previousNode.childrenExpanded : undefined;

        if (childrenCount === 0) {
          rowTreeNodeConfig = {
            id: rowId,
            depth: parentDepth + 1,
            parent: parentId,
            type: 'leaf',
            groupingKey,
          };
        } else {
          const children: GridRowId[] = [];
          for (let j = 0; j < childrenCount; j += 1) {
            const skeletonId = getSkeletonNestedRowId(j, rowId);
            children.push(skeletonId);

            const skeletonRowNode: GridSkeletonRowNode = {
              type: 'skeletonRow',
              id: skeletonId,
              parent: rowId,
              depth: parentDepth + 2,
            };

            tree[skeletonId] = skeletonRowNode;
          }
          const groupNode: GridDataSourceGroupNode = {
            id: rowId,
            depth: parentDepth + 1,
            parent: parentId,
            type: 'group',
            groupingKey,
            path: [...parentPath, groupingKey] as unknown as string[],
            children,
            isAutoGenerated: false,
            groupingField: props.treeData ? null : (groupFields[parentPath.length] ?? null),
            serverChildrenCount: childrenCount,
            childrenFromPath: Object.create(null),
          };
          const childrenExpanded = checkGroupChildrenExpansion(
            groupNode,
            props.defaultGroupingExpansionDepth,
            maxDepth,
            props.isGroupExpandedByDefault,
            previousChildrenExpanded,
          );

          rowTreeNodeConfig = {
            ...groupNode,
            childrenExpanded,
          };

          hasExpandedGroupToFetch ||= childrenExpanded === true;
        }
        dataRowIdToModelLookup[rowId] = rowModel;
        tree[rowId] = rowTreeNodeConfig;

        // Update parent's childrenFromPath lookup
        const groupingFieldName =
          (rowTreeNodeConfig as GridDataSourceGroupNode).groupingField ?? '__no_field__';
        const groupingKeyName = rowTreeNodeConfig.groupingKey ?? '__no_key__';
        if (!targetGroupChildrenFromPath[groupingFieldName]) {
          targetGroupChildrenFromPath[groupingFieldName] = {};
        }
        targetGroupChildrenFromPath[groupingFieldName][groupingKeyName.toString()] = rowId;

        seenIds.add(rowId);
      }

      // Keep skeleton rows in place (for both root and nested parents) instead of
      // filtering them out. Dropping them here would collapse any unfetched gap
      // above the replaced range — e.g. after a jump/fast scroll that loads a
      // middle range while the rows above it are still skeletons — because
      // `addRootSkeletonRows` only appends padding at the end. Leaving the
      // skeletons at their indices keeps loaded rows at their real positions.
      // The sort/filter reset path starts from an empty children list, so there are no
      // skeletons to preserve and `addRootSkeletonRows` pads normally.

      // The replacement above only overwrites `response.rows.length` entries, so anything past
      // the new row count is a row the server dropped, or a skeleton that would never resolve.
      trimChildrenToRowCount(
        tree,
        dataRowIdToModelLookup,
        targetGroupChildren,
        targetGroupChildrenFromPath,
        response.rowCount,
        seenIds,
      );

      const updatedGroup = {
        ...targetGroup,
        children: targetGroupChildren,
        childrenFromPath: targetGroupChildrenFromPath,
      } as GridDataSourceGroupNode;
      if (parentId !== GRID_ROOT_GROUP_ID && response.rowCount !== undefined) {
        updatedGroup.serverChildrenCount = response.rowCount;
      }
      tree[parentId] = updatedGroup;

      // Removes potential remaining skeleton rows from the dataRowIds.
      // For the root parent the targetGroupChildren list IS the full root row order,
      // so we can replace dataRowIds wholesale. For nested parents it only contains
      // that subtree's children, so we merge into the existing list to avoid wiping
      // sibling subtrees' row IDs.
      let dataRowIds: GridRowId[];
      if (parentId === GRID_ROOT_GROUP_ID) {
        dataRowIds = targetGroupChildren.filter((childId) => tree[childId]?.type !== 'skeletonRow');
      } else {
        const newIds = new Set(
          targetGroupChildren.filter((childId) => tree[childId]?.type !== 'skeletonRow'),
        );
        const previousIds = privateApiRef.current.state.rows.dataRowIds;
        dataRowIds = previousIds.filter(
          (id) => tree[id] !== undefined && tree[id].type !== 'skeletonRow' && !newIds.has(id),
        );
        newIds.forEach((id) => dataRowIds.push(id));
      }

      privateApiRef.current.caches.rows.dataRowIdToModelLookup = dataRowIdToModelLookup;

      privateApiRef.current.setState((state) => ({
        ...state,
        rows: {
          ...state.rows,
          dataRowIdToModelLookup,
          dataRowIds,
          tree: { ...tree },
          treeDepths: computeTreeDepths(tree),
          totalRowCount:
            parentId === GRID_ROOT_GROUP_ID ? (response.rowCount ?? -1) : state.rows.totalRowCount,
        },
      }));
      privateApiRef.current.publishEvent('rowsSet');

      return hasExpandedGroupToFetch;
    },
    [
      privateApiRef,
      getGroupKey,
      getChildrenCount,
      props.treeData,
      props.defaultGroupingExpansionDepth,
      props.isGroupExpandedByDefault,
    ],
  );

  const removeDuplicateRows = React.useCallback(
    (rows: GridGetRowsResponse['rows'], parentId: GridRowId = GRID_ROOT_GROUP_ID) => {
      const tree = { ...privateApiRef.current.state.rows.tree };
      const dataRowIdToModelLookup = { ...privateApiRef.current.state.rows.dataRowIdToModelLookup };
      const parentNode = tree[parentId] as GridGroupNode | undefined;
      if (!parentNode || parentNode.type !== 'group') {
        return;
      }
      const parentChildren = [...parentNode.children];
      const isRoot = parentId === GRID_ROOT_GROUP_ID;
      const skeletonDepth = isRoot ? 0 : parentNode.depth + 1;

      let duplicateRowCount = 0;
      rows.forEach((row) => {
        const rowId = gridRowIdSelector(privateApiRef, row);
        if (tree[rowId] || dataRowIdToModelLookup[rowId]) {
          const index = parentChildren.indexOf(rowId);
          if (index !== -1) {
            const skeletonId = isRoot
              ? getSkeletonRowId(index)
              : getSkeletonNestedRowId(index, parentId);
            parentChildren[index] = skeletonId;
            tree[skeletonId] = {
              type: 'skeletonRow',
              id: skeletonId,
              parent: parentId,
              depth: skeletonDepth,
            };
          }
          // Remove the duplicate together with any subtree it owned.
          // The following `replaceNestedRows` recomputes `dataRowIds` from the tree and re-inserts it.
          deleteRowAndDescendants(tree, dataRowIdToModelLookup, rowId);
          duplicateRowCount += 1;
        }
      });

      if (duplicateRowCount > 0) {
        tree[parentId] = { ...parentNode, children: parentChildren };
        privateApiRef.current.setState((state) => ({
          ...state,
          rows: {
            ...state.rows,
            tree,
            treeDepths: computeTreeDepths(tree),
            dataRowIdToModelLookup,
          },
        }));
      }
    },
    [privateApiRef],
  );

  const updateLoadedRows = React.useCallback(
    (parentId: GridRowId, startIndex: number, rows: GridGetRowsResponse['rows']) => {
      // Nothing to replace, so the caller prunes instead of rebuilding the range.
      if (rows.length === 0) {
        return true;
      }

      const tree = privateApiRef.current.state.rows.tree;
      const parentNode = tree[parentId] as GridGroupNode | undefined;
      if (!parentNode || parentNode.type !== 'group') {
        return false;
      }

      const firstTargetRow = parentNode.children[startIndex];
      if (!firstTargetRow || tree[firstTargetRow]?.type === 'skeletonRow') {
        return false;
      }

      const existingRowIds = parentNode.children.slice(startIndex, startIndex + rows.length);
      const newRowIds = rows.map((row) => gridRowIdSelector(privateApiRef, row));
      const sameRowIds =
        existingRowIds.length === newRowIds.length &&
        existingRowIds.every((rowId, index) => rowId === newRowIds[index]);

      if (!sameRowIds) {
        return false;
      }

      const dataRowIdToModelLookup = privateApiRef.current.state.rows.dataRowIdToModelLookup;
      const changedRows = rows.filter((row, index) => {
        const existingRow = dataRowIdToModelLookup[existingRowIds[index]];
        return !isDeepEqual(row, existingRow);
      });

      if (changedRows.length > 0) {
        const parentPath =
          parentId === GRID_ROOT_GROUP_ID
            ? []
            : ((parentNode as GridDataSourceGroupNode).path ?? []);
        privateApiRef.current.updateNestedRows(changedRows, parentPath);
      }

      return true;
    },
    [privateApiRef],
  );

  const handleDataUpdate = React.useCallback<GridStrategyProcessor<'dataSourceRootRowsUpdate'>>(
    (params) => {
      if ('error' in params) {
        return;
      }

      const { response, fetchParams } = params;
      let hasExpandedGroupToFetch = false;
      const wasStale = rowsStale.current;
      const pageRowCount = privateApiRef.current.state.pagination.rowCount;
      if (
        (fetchParams as GridGetRowsParams).groupKeys?.length === 0 &&
        (response.rowCount !== undefined || pageRowCount === undefined)
      ) {
        privateApiRef.current.setRowCount(response.rowCount === undefined ? -1 : response.rowCount);
      }

      // scroll to the top if the rows are stale and the new request is for the first page
      if (rowsStale.current && params.fetchParams.start === 0) {
        privateApiRef.current.scroll({ top: 0 });
        // Keep the nested lazy-loading tree shape after sort/filter.
        // Plain `setRows()` would recreate expandable groups without child skeleton rows.
        const previousTree = resetRowTree();
        previousTreeRef.current = previousTree;
        hasExpandedGroupToFetch = replaceNestedRows(
          0,
          response,
          fetchParams,
          GRID_ROOT_GROUP_ID,
          previousTree,
        );
      } else {
        const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(privateApiRef);

        const startingIndex =
          typeof fetchParams.start === 'string'
            ? Math.max(filteredSortedRowIds.indexOf(fetchParams.start), 0)
            : fetchParams.start;

        if (updateLoadedRows(GRID_ROOT_GROUP_ID, startingIndex, response.rows)) {
          // `updateLoadedRows` keeps the rows in place, so it never drops the ones the server did.
          pruneRowsToRowCount(GRID_ROOT_GROUP_ID, response.rowCount);
        } else {
          removeDuplicateRows(response.rows);
          hasExpandedGroupToFetch = replaceNestedRows(startingIndex, response, fetchParams);
        }
      }

      rowsStale.current = false;

      addRootSkeletonRows();
      privateApiRef.current.setLoading(false);
      privateApiRef.current.unstable_applyPipeProcessors(
        'processDataSourceRows',
        { params: params.fetchParams, response },
        false,
      );
      privateApiRef.current.requestPipeProcessorsApplication('hydrateRows');
      // After sort/filter the tree is reset and skeletons are re-added by
      // `addRootSkeletonRows`, but the viewport's first/last indices may be unchanged
      // (e.g. scrolled to top already) so `renderedRowsIntervalChange` does not fire.
      // Trigger a scan so root-level skeletons sitting in the viewport get fetched.
      if (hasExpandedGroupToFetch || wasStale) {
        fetchVisibleSkeletonRows({ skipFallbackRevalidation: true });
      }
      startPolling();
    },
    [
      privateApiRef,
      addRootSkeletonRows,
      replaceNestedRows,
      removeDuplicateRows,
      updateLoadedRows,
      pruneRowsToRowCount,
      fetchVisibleSkeletonRows,
      startPolling,
      resetRowTree,
    ],
  );

  const handleNestedDataUpdate = React.useCallback<
    GridStrategyProcessor<'dataSourceNestedRowsUpdate'>
  >(
    (params) => {
      if ('error' in params) {
        return;
      }
      const { parentId, fetchParams, response } = params;

      // Get the relative start index from fetchParams
      const startIndex = typeof fetchParams.start === 'number' ? fetchParams.start : 0;
      let hasExpandedGroupToFetch = false;
      if (updateLoadedRows(parentId, startIndex, response.rows)) {
        pruneRowsToRowCount(parentId, response.rowCount);
      } else {
        removeDuplicateRows(response.rows, parentId);
        hasExpandedGroupToFetch = replaceNestedRows(
          startIndex,
          response,
          fetchParams,
          parentId,
          previousTreeRef.current ?? undefined,
        );
      }
      if (hasExpandedGroupToFetch) {
        fetchVisibleSkeletonRows({ skipFallbackRevalidation: true });
      }
      startPolling();
    },
    [
      replaceNestedRows,
      removeDuplicateRows,
      updateLoadedRows,
      pruneRowsToRowCount,
      fetchVisibleSkeletonRows,
      startPolling,
    ],
  );

  const handleRowCountChange = React.useCallback(
    (newRowCount: number) => {
      if (rowsStale.current) {
        return;
      }

      // Show error if unknown row-count
      if (newRowCount < 0) {
        throw new Error(
          'MUI X Data Grid: Row count is unknown. Please provide a valid row count for lazy loading to work.',
        );
      }

      addRootSkeletonRows();
      privateApiRef.current.requestPipeProcessorsApplication('hydrateRows');
    },
    [privateApiRef, addRootSkeletonRows],
  );

  const handleRenderedRowsIntervalChange = React.useCallback<
    GridEventListener<'renderedRowsIntervalChange'>
  >(
    (params) => {
      if (rowsStale.current) {
        return;
      }

      if (
        renderedRowsIntervalCache.current.firstRowToRender === params.firstRowIndex &&
        renderedRowsIntervalCache.current.lastRowToRender === params.lastRowIndex
      ) {
        return;
      }

      findSkeletonSectionAndFetchRows(params.firstRowIndex, params.lastRowIndex);

      renderedRowsIntervalCache.current = {
        firstRowToRender: params.firstRowIndex,
        lastRowToRender: params.lastRowIndex,
      };
    },
    [findSkeletonSectionAndFetchRows],
  );

  const handleRowExpansionChange = React.useCallback<GridEventListener<'rowExpansionChange'>>(
    (node) => {
      // A manual expand/collapse ends the post-sort/filter restoration cascade.
      // Drop the pre-reset snapshot so subsequent nested fetches read expansion state
      // from the live tree instead of re-applying stale expansion from before the last reset.
      previousTreeRef.current = null;
      if (node.childrenExpanded) {
        fetchVisibleSkeletonRows();
        return;
      }
      cleanUpParentNodeAndGenerateSkeletonRows(node.id);
      // Collapsing shifts rows below the group up into the viewport without firing
      // `renderedRowsIntervalChange` (same indices, different content), so scan again
      // for skeletons now exposed in the cached viewport range.
      fetchVisibleSkeletonRows();
    },
    [fetchVisibleSkeletonRows, cleanUpParentNodeAndGenerateSkeletonRows],
  );

  const throttledHandleRenderedRowsIntervalChange = React.useMemo(
    () => throttle(handleRenderedRowsIntervalChange, props.lazyLoadingRequestThrottleMs),
    [props.lazyLoadingRequestThrottleMs, handleRenderedRowsIntervalChange],
  );

  React.useEffect(() => {
    return () => {
      throttledHandleRenderedRowsIntervalChange.clear();
      stopPolling();
    };
  }, [throttledHandleRenderedRowsIntervalChange, stopPolling]);

  React.useEffect(() => {
    if (!isStrategyActive || props.dataSourceRevalidateMs <= 0) {
      stopPolling();
    }
  }, [isStrategyActive, props.dataSourceRevalidateMs, stopPolling]);

  const handleGridSortModelChange = React.useCallback<GridEventListener<'sortModelChange'>>(
    (newSortModel) => {
      rowsStale.current = true;
      renderedRowsIntervalCache.current = INTERVAL_CACHE_INITIAL_STATE;
      throttledHandleRenderedRowsIntervalChange.clear();
      stopPolling();
      const paginationModel = gridPaginationModelSelector(privateApiRef);
      const filterModel = gridFilterModelSelector(privateApiRef);

      const getRowsParams: GridGetRowsParams = {
        start: 0,
        end: paginationModel.pageSize - 1,
        sortModel: newSortModel,
        filterModel,
      };

      privateApiRef.current.setLoading(true);
      debouncedFetchRows(getRowsParams);
    },
    [privateApiRef, debouncedFetchRows, throttledHandleRenderedRowsIntervalChange, stopPolling],
  );

  const handleGridFilterModelChange = React.useCallback<GridEventListener<'filterModelChange'>>(
    (newFilterModel) => {
      if (!hasFilterModelChanged(newFilterModel)) {
        return;
      }

      rowsStale.current = true;
      renderedRowsIntervalCache.current = INTERVAL_CACHE_INITIAL_STATE;
      throttledHandleRenderedRowsIntervalChange.clear();
      stopPolling();

      const paginationModel = gridPaginationModelSelector(privateApiRef);
      const sortModel = gridSortModelSelector(privateApiRef);
      const getRowsParams: GridGetRowsParams = {
        start: 0,
        end: paginationModel.pageSize - 1,
        sortModel,
        filterModel: newFilterModel,
      };

      privateApiRef.current.setLoading(true);
      debouncedFetchRows(getRowsParams);
    },
    [
      privateApiRef,
      debouncedFetchRows,
      throttledHandleRenderedRowsIntervalChange,
      stopPolling,
      hasFilterModelChanged,
    ],
  );

  const handleDragStart = React.useCallback<GridEventListener<'rowDragStart'>>((row) => {
    draggedRowId.current = row.id;
  }, []);

  const handleDragEnd = React.useCallback<GridEventListener<'rowDragEnd'>>(() => {
    draggedRowId.current = null;
  }, []);

  const handleStrategyActivityChange = React.useCallback<
    GridEventListener<'strategyAvailabilityChange'>
  >(() => {
    setIsStrategyActive(
      privateApiRef.current.getActiveStrategy(GridStrategyGroup.DataSource) ===
        DataSourceRowsUpdateStrategy.LazyLoadedGroupedData,
    );
  }, [privateApiRef]);

  useGridRegisterStrategyProcessor(
    privateApiRef,
    DataSourceRowsUpdateStrategy.LazyLoadedGroupedData,
    'dataSourceRootRowsUpdate',
    handleDataUpdate,
  );

  useGridRegisterStrategyProcessor(
    privateApiRef,
    DataSourceRowsUpdateStrategy.LazyLoadedGroupedData,
    'dataSourceNestedRowsUpdate',
    handleNestedDataUpdate,
  );

  useGridEvent(privateApiRef, 'strategyAvailabilityChange', handleStrategyActivityChange);

  useGridEvent(privateApiRef, 'rowCountChange', runIf(isStrategyActive, handleRowCountChange));
  useGridEvent(
    privateApiRef,
    'rowExpansionChange',
    runIf(isStrategyActive, handleRowExpansionChange),
  );
  useGridEvent(
    privateApiRef,
    'renderedRowsIntervalChange',
    runIf(isStrategyActive, throttledHandleRenderedRowsIntervalChange),
  );
  useGridEvent(
    privateApiRef,
    'sortModelChange',
    runIf(isStrategyActive, handleGridSortModelChange),
  );
  useGridEvent(
    privateApiRef,
    'filterModelChange',
    runIf(isStrategyActive, handleGridFilterModelChange),
  );
  useGridEvent(privateApiRef, 'rowDragStart', runIf(isStrategyActive, handleDragStart));
  useGridEvent(privateApiRef, 'rowDragEnd', runIf(isStrategyActive, handleDragEnd));

  React.useEffect(() => {
    setStrategyAvailability();
  }, [setStrategyAvailability]);
};
